/**
 * Markup and margin.
 *
 * Both express profit as a share of something: markup is profit ÷ cost, margin (gross
 * margin, or profit margin on a single item) is profit ÷ selling price. They are linked by
 *
 *   margin = markup ÷ (1 + markup)        markup = margin ÷ (1 − margin)
 *
 * so a 25% markup is a 20% margin, and a 100% markup is only a 50% margin. Rates are held
 * as decimals (0.25 = 25%). A price below cost gives a negative markup and margin.
 */

export type MarkupMode =
  | "cost-price"
  | "cost-markup"
  | "cost-margin"
  | "price-markup"
  | "price-margin"
  | "markup-to-margin"
  | "margin-to-markup";

export const MARKUP_MODES: readonly MarkupMode[] = [
  "cost-price",
  "cost-markup",
  "cost-margin",
  "price-markup",
  "price-margin",
  "markup-to-margin",
  "margin-to-markup",
];

export interface MarkupResult {
  cost: number;
  price: number;
  profit: number;
  /** Profit ÷ cost, as a decimal. */
  markup: number;
  /** Profit ÷ price, as a decimal. */
  margin: number;
}

function build(cost: number, price: number): MarkupResult {
  const profit = price - cost;
  return { cost, price, profit, markup: profit / cost, margin: profit / price };
}

/** Margin as a decimal from markup as a decimal; null when markup is −100% or lower. */
export function markupToMargin(markup: number): number | null {
  if (!Number.isFinite(markup) || markup <= -1) {
    return null;
  }
  return markup / (1 + markup);
}

/** Markup as a decimal from margin as a decimal; null when margin is 100% or higher. */
export function marginToMarkup(margin: number): number | null {
  if (!Number.isFinite(margin) || margin >= 1) {
    return null;
  }
  return margin / (1 - margin);
}

export function fromCostPrice(
  cost: number,
  price: number
): MarkupResult | null {
  if (!(cost > 0 && price > 0)) {
    return null;
  }
  return build(cost, price);
}

export function fromCostMarkup(
  cost: number,
  markup: number
): MarkupResult | null {
  if (!(cost > 0) || markup <= -1) {
    return null;
  }
  return build(cost, cost * (1 + markup));
}

export function fromCostMargin(
  cost: number,
  margin: number
): MarkupResult | null {
  if (!(cost > 0) || margin >= 1) {
    return null;
  }
  return build(cost, cost / (1 - margin));
}

export function fromPriceMarkup(
  price: number,
  markup: number
): MarkupResult | null {
  if (!(price > 0) || markup <= -1) {
    return null;
  }
  return build(price / (1 + markup), price);
}

export function fromPriceMargin(
  price: number,
  margin: number
): MarkupResult | null {
  if (!(price > 0) || margin >= 1) {
    return null;
  }
  return build(price * (1 - margin), price);
}

/** One row of the markup ↔ margin reference table. */
export interface ConversionRow {
  /** The rate the row starts from, as a decimal. */
  from: number;
  /** The equivalent rate, as a decimal. */
  to: number;
}

export const COMMON_MARGINS: readonly number[] = [
  0.1, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.75, 0.8, 0.9,
];

export const COMMON_MARKUPS: readonly number[] = [
  0.1, 0.2, 0.25, 0.3, 0.5, 0.75, 1, 1.5, 2, 3,
];

/** Markup for each common margin. */
export function marginTable(): ConversionRow[] {
  return COMMON_MARGINS.map((from) => ({
    from,
    to: marginToMarkup(from) as number,
  }));
}

/** Margin for each common markup. */
export function markupTable(): ConversionRow[] {
  return COMMON_MARKUPS.map((from) => ({
    from,
    to: markupToMargin(from) as number,
  }));
}

/** Scales a per-unit result by a quantity (for totals). */
export function scaleResult(result: MarkupResult, units: number): MarkupResult {
  return {
    ...result,
    cost: result.cost * units,
    price: result.price * units,
    profit: result.profit * units,
  };
}
